import { AuthService } from '../services/AuthService';
import { persistentLogger } from './PersistentLogger';

/**
 * Wrapper around fetch that automatically includes JWT token in Authorization header
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options (same as standard fetch)
 * @returns Promise<Response>
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const authHeader = AuthService.getAuthHeader();
  const hasToken = !!AuthService.getToken();
  
  persistentLogger.log('api', 'authenticatedFetch called', { 
    url, 
    hasToken,
    hasAuthHeader: !!authHeader,
    method: options.method || 'GET'
  });
  
  const headers = new Headers(options.headers);
  
  if (authHeader) {
    headers.set('Authorization', authHeader);
    persistentLogger.log('api', 'Authorization header added to request');
  } else {
    persistentLogger.warn('api', 'No auth header available for request', { hasToken });
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  persistentLogger.log('api', 'Fetch response received', { 
    url,
    status: response.status,
    statusText: response.statusText,
    ok: response.ok
  });

  // If we get a 401, the token is invalid. Clear auth and let React re-render to Login.
  // No full page reload - that was causing "page starts loading then refreshes to blank".
  if (response.status === 401) {
    persistentLogger.error('api', '401 Unauthorized response', {
      url,
      hadToken: hasToken,
      hadAuthHeader: !!authHeader,
      method: options.method || 'GET'
    });

    if (hasToken) {
      persistentLogger.warn('api', '401 with token present - clearing auth (no reload)');
      AuthService.clearAuth();
      // AuthContext will re-render; ProtectedRoute will show Login. No window.location.reload().
    } else {
      persistentLogger.warn('api', '401 without token - this is expected if not logged in');
    }
  }

  return response;
}

/**
 * Helper to make authenticated GET requests
 */
export async function authenticatedGet(url: string, options: RequestInit = {}): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'GET',
  });
}

/**
 * Helper to make authenticated POST requests
 */
export async function authenticatedPost(
  url: string,
  body?: any,
  options: RequestInit = {}
): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Helper to make authenticated PUT requests
 */
export async function authenticatedPut(
  url: string,
  body?: any,
  options: RequestInit = {}
): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Helper to make authenticated DELETE requests
 */
export async function authenticatedDelete(url: string, options: RequestInit = {}): Promise<Response> {
  return authenticatedFetch(url, {
    ...options,
    method: 'DELETE',
  });
}

